import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../app/store";
import SelectDropdownV2 from "../../../components/ui/selectdropdown/v2";
import { OptionNameValuePair } from "../../../lib/interfaces/types";
import { selectFraudElementCountForParty } from "../logic/fraudElementsSlice";
import { getSCNPartyOptions } from "./FraudElementFormData";
import { selectPartiesWithSCNAction } from "../../recommendations/logic/ActionsAgainstPerpetratorsSlice";

interface FraudElementsOverviewProps {
  selectedPartyId?: string;
  onPartyChange: (partyId: string) => void;
}

const FraudElementsOverview = ({
  selectedPartyId,
  onPartyChange,
}: FraudElementsOverviewProps) => {
  const scnParties = useSelector(selectPartiesWithSCNAction);

  const dropdownOptions = useMemo<OptionNameValuePair<string>[]>(
    () =>
      getSCNPartyOptions(scnParties).map((option) => ({
        ...option,
        display_component: (
          <span className="text-[14px] font-medium leading-[14px] text-[#000]">
            {option.name}
          </span>
        ),
        selected_display_component: (
          <div className="flex items-center gap-2 min-w-0">
            <span className="shrink-0 text-[12px] font-normal leading-4 text-[#737373]">
              Party Name
            </span>
            <span className="truncate text-[14px] font-medium text-[#000]">
              {option.name}
            </span>
          </div>
        ),
      })),
    [scnParties],
  );

  const selectedParty = dropdownOptions.find(
    (party) => party.value === selectedPartyId,
  );

  useEffect(() => {
    if (!selectedPartyId && dropdownOptions.length > 0) {
      onPartyChange(dropdownOptions[0].value);
    }
  }, [selectedPartyId, dropdownOptions, onPartyChange]);

  const fraudElementCount = useSelector((state: RootState) =>
    selectFraudElementCountForParty(state, selectedPartyId),
  );

  return (
    <div className="flex items-center justify-between bg-[#E5E7EB] p-3 font-[Inter]">
      <SelectDropdownV2
        name="Party"
        enableSearch={false}
        options={dropdownOptions}
        selectedOption={selectedParty}
        onSelectionChange={(option) => onPartyChange(option.value)}
        containerClassName="w-[320px]"
        selectedContainerClassName="rounded-[8px] border border-[#E5E5E5] bg-white shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]"
        labelClassName="px-3 py-[6px]"
        titleClassName="w-full"
      />

      <div className="flex items-center text-[14px] leading-5">
        <span className="font-medium text-[#0A0A0A]">
          {fraudElementCount}
        </span>

        <span className="ml-1 text-[#737373]">
          Fraud Elements
        </span>
      </div>
    </div>
  );
};

export default FraudElementsOverview;